import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Reveal, Eyebrow } from "./motion";

export default function Contact({ profile, propertyId = null, propertyTitle = null, compact = false }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: propertyTitle ? `I'm interested in ${propertyTitle}.` : "", company: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/inquiries", { ...form, property_id: propertyId, property_title: propertyTitle });
      toast.success("Thank you — your inquiry has been received.");
      setForm({ name: "", email: "", phone: "", message: "", company: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full bg-transparent border-b border-[#0A1128]/25 py-3 text-[#0A1128] placeholder:text-[#0A1128]/40 focus:border-[var(--gold)] focus:outline-none transition-colors";

  const FormEl = (
    <form onSubmit={submit} data-testid="inquiry-form" className="space-y-6">
      <input type="text" name="company" tabIndex={-1} autoComplete="off" value={form.company} onChange={set("company")} className="hidden" aria-hidden="true" />
      <input data-testid="inquiry-name" required placeholder="Full Name" value={form.name} onChange={set("name")} className={inputClass} />
      <div className="grid sm:grid-cols-2 gap-6">
        <input data-testid="inquiry-email" required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={inputClass} />
        <input data-testid="inquiry-phone" placeholder="Phone" value={form.phone} onChange={set("phone")} className={inputClass} />
      </div>
      <textarea data-testid="inquiry-message" required rows={compact ? 3 : 4} placeholder="How can I help?" value={form.message} onChange={set("message")} className={inputClass + " resize-none"} />
      <button data-testid="inquiry-submit" disabled={loading}
        className="w-full sm:w-auto px-10 py-4 bg-[#0A1128] text-[#FAF9F6] text-sm uppercase tracking-[0.14em] hover:bg-[#1a2342] transition-colors duration-300 disabled:opacity-60">
        {loading ? "Sending…" : "Submit Inquiry"}
      </button>
    </form>
  );

  if (compact) return FormEl;

  return (
    <section id="contact" data-testid="contact-section" className="bg-[#FAF9F6] py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-6">
          <Reveal><Eyebrow>Contact</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight text-[#0A1128] mt-5 mb-4 text-balance">
              Let&apos;s find your <span className="italic text-[var(--gold)]">next chapter.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-[var(--slate)] mb-8 max-w-md leading-relaxed">
              Whether buying, selling, or simply exploring possibilities, I welcome the conversation. Every inquiry is answered personally.
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <div data-testid="contact-details" className="mb-8 space-y-3 text-[#0A1128]">
              {profile?.email && (
                <a href={`mailto:${profile.email}`} className="flex items-center gap-3 text-sm hover:text-[var(--gold)] transition-colors">
                  <Mail size={16} className="text-[var(--gold)] shrink-0" /> {profile.email}
                </a>
              )}
              {profile?.phone && (
                <a href={`tel:${profile.phone.replace(/[^0-9+]/g, "")}`} className="flex items-center gap-3 text-sm hover:text-[var(--gold)] transition-colors">
                  <Phone size={16} className="text-[var(--gold)] shrink-0" /> {profile.phone}
                </a>
              )}
              {profile?.address && (
                <p className="flex items-start gap-3 text-sm text-[var(--slate)] max-w-sm leading-relaxed">
                  <MapPin size={16} className="text-[var(--gold)] shrink-0 mt-0.5" /> {profile.address}
                </p>
              )}
            </div>
          </Reveal>
          <Reveal delay={0.2}><div>{FormEl}</div></Reveal>
        </div>
        <Reveal delay={0.2} className="lg:col-span-6 hover-img h-[70vh] hidden lg:block">
          <img src="https://customer-assets-agu9un31.emergentagent.net/job_property-showcase-572/artifacts/x9wnj0yb_Mabel%20McMillan_022_1.webp"
            alt="Mabel McMillan handing over keys" className="w-full h-full object-cover" />
        </Reveal>
      </div>
    </section>
  );
}
